<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";
  import { formatDate } from "../../lib/utils/format";

  let { trip, user, schedules = [], planningLink = '', qrCode = '' }: {
    trip: any;
    user: any;
    schedules?: any[];
    planningLink?: string;
    qrCode?: string;
  } = $props();

  let copied = $state(false);

  function formatTime(dateStr: string): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    });
  }

  function copyLink() {
    if (planningLink) {
      navigator.clipboard.writeText(planningLink);
      copied = true;
      setTimeout(() => copied = false, 2000);
    }
  }

  function openPlanner() {
    if (planningLink) {
      window.open(planningLink, '_blank');
    }
  }

  // Group schedules by date
  const groupedSchedules = $derived(
    Object.entries(
      schedules.reduce((groups: Record<string, any[]>, s) => {
        const date = s.date || s.start_time?.split('T')[0];
        if (!groups[date]) groups[date] = [];
        groups[date].push(s);
        return groups;
      }, {})
    ).sort(([a], [b]) => a.localeCompare(b))
  );
</script>

<TripSubNav {trip} currentPage="plan" />

<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
    <!-- Left: QR Code & Link -->
    <div class="space-y-6">
      <div class="bg-white/5 rounded-xl p-6 border border-white/10">
        <h2 class="text-lg font-medium mb-4">📱 PC에서 편집하기</h2>
        <p class="text-sm text-muted mb-6">
          휴대폰에서 QR 코드를 스캔하거나 링크를 복사하여 PC에서 일정을 편집하세요.
        </p>

        <!-- QR Code -->
        {#if qrCode}
          <div class="flex justify-center mb-6">
            <div class="bg-white p-4 rounded-xl">
              <img src={qrCode} alt="QR Code" class="w-48 h-48" />
            </div>
          </div>
        {:else}
          <div class="flex justify-center mb-6">
            <div class="w-48 h-48 bg-white/10 rounded-xl flex items-center justify-center">
              <span class="text-4xl">📱</span>
            </div>
          </div>
        {/if}

        <!-- Link -->
        <div class="space-y-3">
          <div class="flex gap-2">
            <input 
              type="text" 
              value={planningLink} 
              readonly
              class="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <Button variant="secondary" onclick={copyLink}>
              {copied ? '✓ 복사됨' : '복사'}
            </Button>
          </div>
          <Button onclick={openPlanner} class="w-full">
            🚀 PC 플래너 열기
          </Button>
        </div>
      </div>

      <!-- Tips -->
      <div class="bg-teal-500/10 rounded-xl p-4 border border-teal-500/20">
        <h3 class="text-sm font-medium text-teal-400 mb-2">💡 팁</h3>
        <ul class="text-sm text-muted space-y-1">
          <li>• PC에서 더 편리하게 일정을 편집할 수 있습니다</li>
          <li>• 변경사항은 실시간으로 동기화됩니다</li>
          <li>• 링크는 24시간 동안 유효합니다</li>
        </ul>
      </div>
    </div>

    <!-- Right: Schedule Preview -->
    <div class="bg-white/5 rounded-xl p-6 border border-white/10">
      <h2 class="text-lg font-medium mb-4">📋 일정 미리보기</h2>
      
      {#if schedules.length === 0}
        <div class="text-center py-12 text-muted">
          <div class="text-4xl mb-3">🗓️</div>
          <p>등록된 일정이 없습니다.</p>
        </div>
      {:else}
        <div class="space-y-4 max-h-[500px] overflow-y-auto">
          {#each groupedSchedules as [date, daySchedules]}
            <div class="border-l-2 border-teal-500/30 pl-4">
              <h3 class="text-sm font-medium text-teal-400 mb-2">
                {formatDate(date, 'long')}
              </h3>
              <div class="space-y-2">
                {#each daySchedules.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')) as schedule}
                  <div class="bg-white/5 rounded-lg p-3">
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-muted">
                        {formatTime(schedule.start_time)}
                      </span>
                      <span class="font-medium text-sm">{schedule.title}</span>
                    </div>
                    {#if schedule.location}
                      <p class="text-xs text-muted mt-1">
                        📍 {schedule.location}
                      </p>
                    {/if}
                    {#if schedule.memo}
                      <p class="text-xs text-muted mt-1">
                        {schedule.memo}
                      </p>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>
